import { downloadBlob, formatMinutes } from '@src/common';
import { device } from '@src/common/breakpoints';
import { SaleDate } from '@src/components/Account/shared';
import FeedbackMessage from '@src/components/ui/FeedbackMessage';
import { Order, SaleItem, Voucher, VoucherStatus } from '@src/core/buyerApiClient';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { LayoutColors } from '@src/core/theme';
import { orderVouchersSelector, orderVoucherInfoSelector } from '@src/features/order';
import { loadRescheduleAvailableDates, saleItemRescheduleOngoingSelector } from '@src/features/sale';
import { BodyExtraSmall, BodyRegularMedium, BodySmallMedium, BodySmallSummary } from '@src/fonts/typography/Typography';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { getVoucher } from '@src/common/vouchers';
import { track } from '@src/services/analytics';
import Reschedule from './Reschedule';
import ReservationTable from './ReservationTable';
import HeaderDownloadButton from './ReservationDownloadButton';
import RescheduleButton from './RescheduleButton';
import { appInfoSelector } from '@src/features/auth';
import { Tooltip } from '@src/components/reusable/Tooltip';

type ReservationProps = {
  order?: Order;
  saleItem: SaleItem;
  hasMultipleVouchers: boolean;
  disableReschedule?: boolean;
  isPackageItem?: boolean;
};

interface LoadingVoucherType {
  [key: string]: boolean;
}

const Reservation = (props: ReservationProps) => {
  const { order, saleItem, hasMultipleVouchers, disableReschedule = false, isPackageItem = false } = props;
  const { id, product, canReschedule, scheduleDate, scheduleTimeMinutes } = saleItem;
  const appInfo = useAppSelector(appInfoSelector);

  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const [rescheduling, setRescheduling] = useState({
    isDone: false,
    isGoing: false,
  });
  const reservationRescheduleOngoing = useAppSelector(saleItemRescheduleOngoingSelector);

  const orderVouchers = useAppSelector(orderVouchersSelector(order?.id ?? ''));

  const vouchers = useMemo(() => {
    return orderVouchers.filter((voucher) => voucher.saleItemId === saleItem.id);
  }, [orderVouchers]);

  const hasPendingVouchers = useMemo(
    () => vouchers.some((voucher) => voucher.status === VoucherStatus.Pending),
    [vouchers],
  );

  const voucherSplitPolicy = useMemo(() => (vouchers.length === 1 ? 'normal' : 'split'), [vouchers]);

  const canShowReschedule = useMemo(
    () => canReschedule && !rescheduling.isGoing,
    [canReschedule, rescheduling.isGoing],
  );

  const shouldShowReservationActions = useMemo(
    () => !hasMultipleVouchers || voucherSplitPolicy || canShowReschedule,
    [hasMultipleVouchers, voucherSplitPolicy, canShowReschedule],
  );

  const [loadingVoucherPdf, setLoadingVoucherPdf] = useState<LoadingVoucherType>({});

  const handleReschedulingState = useCallback(() => {
    setRescheduling((state) => ({ ...state, isGoing: true }));
    dispatch(loadRescheduleAvailableDates(id));
  }, [id]);

  const handleOpenVoucherPdfButtonClick = async (voucher: Voucher) => {
    track('saleViewVoucher', { saleItemId: saleItem.id });

    setLoadingVoucherPdf({ ...loadingVoucherPdf, [voucher.id]: true });

    const pdfBlob = await getVoucher(voucher);

    if (pdfBlob) {
      downloadBlob(pdfBlob, `voucher-${voucher.id}.pdf`);
    }

    setLoadingVoucherPdf({ ...loadingVoucherPdf, [voucher.id]: false });
  };

  const isVoucherPdfLoading = (voucher: Voucher) => !!loadingVoucherPdf[voucher.id];

  if (!vouchers.length) {
    return null;
  }

  return (
    <>
      <ReservationContainer>
        <ReservationHeader>
          <div id='title'>
            <h4>{product.name}</h4>

            {product.appId !== appInfo?.id ? (
              <ProductProvider>{i18n('product_package_adder_sold_by', { owner: product.app.name })}</ProductProvider>
            ) : null}

            <div>
              <SaleDate
                scheduleDate={scheduleDate}
                scheduleTimeMinutes={scheduleTimeMinutes ? formatMinutes(Number(scheduleTimeMinutes)) : ''}
              />
            </div>
          </div>

          {shouldShowReservationActions && (
            <Actions>
              {canShowReschedule ? (
                <RescheduleButton
                  disabled={disableReschedule}
                  onClick={handleReschedulingState}
                  isPackageItem={isPackageItem}
                />
              ) : null}

              <Tooltip
                arrow
                placement='top'
                showContent={hasPendingVouchers}
                label={i18n('account_sale_download_all_vouchers_tooltip')}
              >
                <HeaderDownloadButton
                  hasMultipleVouchers={hasMultipleVouchers}
                  text={i18n('reservation_download')}
                  isVoucherPdfLoading={isVoucherPdfLoading}
                  onOpenVoucherPdfButtonClick={handleOpenVoucherPdfButtonClick}
                  vouchers={vouchers}
                />
              </Tooltip>
            </Actions>
          )}
        </ReservationHeader>

        {rescheduling.isDone && !reservationRescheduleOngoing ? (
          <FeedbackMessage message={i18n('account_sale_reschedule_success_message')} type='success' />
        ) : null}

        {rescheduling.isDone && reservationRescheduleOngoing ? <span>{i18n('reservation_rescheduling')}</span> : null}

        <ReservationTable
          hasMultipleVouchers={hasMultipleVouchers}
          saleItem={saleItem}
          vouchers={vouchers}
          onOpenVoucherPdf={handleOpenVoucherPdfButtonClick}
          isVoucherPdfLoading={isVoucherPdfLoading}
        />
      </ReservationContainer>

      {canReschedule && rescheduling.isGoing ? (
        <Reschedule
          product={product}
          reservationId={id}
          scheduleTimeMinutes={scheduleTimeMinutes}
          onCancel={() => setRescheduling({ isDone: false, isGoing: false })}
          onConfirm={() => setRescheduling({ isDone: true, isGoing: false })}
        />
      ) : null}
    </>
  );
};

export default Reservation;

const ReservationContainer = styled.section`
  width: 100%;
  background: ${LayoutColors.whiteLight};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px;
  max-width: 828px;
  border-radius: 8px;
  align-self: stretch;
  flex-grow: 0;

  ${device.smallTablet} {
    padding: 16px;
  }
`;
const ReservationHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${LayoutColors.grayMain};

  div#title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding-bottom: 8px;
    width: 100%;

    h4 {
      ${BodyRegularMedium};
      color: ${LayoutColors.blackMain};
    }

    div {
      display: flex;
      flex-direction: row;
      gap: 8px;

      span {
        ${BodySmallSummary};
        color: ${LayoutColors.blackMain};
      }

      span#voucher_consumed_date {
        font-weight: 400 !important;
      }
    }
  }

  ${device.smallTablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    width: 100%;
    border-bottom: none;

    div#title {
      border-bottom: none;
      padding-bottom: 0;

      div {
        span {
          ${BodySmallMedium};
        }
      }
    }
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 16px;

  ${device.smallTablet} {
    flex-direction: row;
    align-items: center;
    max-width: fit-content;
    gap: 8px;
  }
`;

const ProductProvider = styled.p`
  ${BodyExtraSmall};
  color: ${LayoutColors.blackMain};
  margin-top: -8px;
`;
